import React, { useState, useEffect } from "react"
import {
  DRIVER_EMERGENCIES_SEARCH,
  DRIVERS_SEARCH,
  PARTNERS_SEARCH,
  PARTNERS_BRANCH_SEARCH,
  EMERGENCY_TYPES_SEARCH,
  EMERGENCY_SUB_TYPES_SEARCH,
} from "../../helpers/api_endpoints"
import { post } from "../../helpers/api_helper"
import {
  Table,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Alert,
  Label,
  Input,
  Badge,
  FormGroup,
} from "reactstrap"
import moment from "moment"

import { Link, useLocation, useNavigate } from "react-router-dom"
import { Trans, useTranslation } from "react-i18next"
import Select from "react-select"
import { ITEMS_PER_PAGE } from "helpers/pagination_helper"

document.title = "Driver Emergencies"

function DriverEmergenciesList() {
  const { i18n } = useTranslation()
  const [driverEmergencies, setDriverEmergencies] = useState([])
  const [successMsg, setSuccessMsg] = useState("")
  const [filters, setFilters] = useState({})
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(0)
  const [loading, setLoading] = useState(false)
  const [drivers, setDrivers] = useState([])
  const [partners, setPartners] = useState([])
  const [partnersBranches, setPartnersBranches] = useState([])
  const [emergencyTypes, setEmergencyTypes] = useState([])
  const [emergencySubTypes, setEmergencySubTypes] = useState([])
  const [statuses, setStatuses] = useState([
    { value: "all", label: "All" },
    { value: "pending", label: "Pending" },
    { value: "approved", label: "Approved" },
    { value: "rejected", label: "Rejected" },
  ])

  useEffect(() => {
    const getDriverEmergencies = async () => {
      setLoading(true)
      try {
        const response = await post(
          `${DRIVER_EMERGENCIES_SEARCH}?paginate=${ITEMS_PER_PAGE}&page=${page}`,
          filters
        )
        const data = response.data
        setDriverEmergencies(data.data)
        setTotalPages(data.pagination.totalPages)
        setPage(data.pagination.currentPage)
        setLoading(false)
      } catch (error) {
        setLoading(false)
        console.log(error)
      }
    }
    getDriverEmergencies()
  }, [page, filters])

  useEffect(() => {
    const getDrivers = async () => {
      try {
        const response = await post(`${DRIVERS_SEARCH}`)
        const data = response.data
        setDrivers(data)
        setLoading(false)
      } catch (error) {
        setLoading(false)
        console.log("Error fetching drivers:", error)
      }
    }

    const getPartners = async () => {
      try {
        const response = await post(`${PARTNERS_SEARCH}`)
        const data = response.data
        setPartners(data)
        setLoading(false)
      } catch (error) {
        setLoading(false)
        console.log("Error fetching partners:", error)
      }
    }

    const getPartnersBranches = async () => {
      try {
        const response = await post(`${PARTNERS_BRANCH_SEARCH}`)
        const data = response.data
        setPartnersBranches(data)
        setLoading(false)
      } catch (error) {
        setLoading(false)
        console.log("Error fetching partners branches:", error)
      }
    }

    const getEmergencyTypes = async () => {
      try {
        const response = await post(`${EMERGENCY_TYPES_SEARCH}`)
        const data = response.data
        setEmergencyTypes(data)
        setLoading(false)
      } catch (error) {
        setLoading(false)
        console.log("Error fetching emergency types:", error)
      }
    }

    const getEmergencySubTypes = async () => {
      try {
        const response = await post(`${EMERGENCY_SUB_TYPES_SEARCH}`)
        const data = response.data
        setEmergencySubTypes(data)
        setLoading(false)
      } catch (error) {
        setLoading(false)
        console.log("Error fetching emergency sub types:", error)
      }
    }
    getDrivers()
    getPartners()
    getPartnersBranches()
    getEmergencyTypes()
    getEmergencySubTypes()
  }, [])

  const handleFilter = (type, value) => {
    const filtvalues = { ...filters }
    if (value === "") {
      delete filtvalues[type]
    } else {
      switch (type) {
        case "driverId":
          filtvalues.driverId = value
          break
        case "partnerId":
          filtvalues.partnerId = value
          break
        case "partnerBranchId":
          filtvalues.partnerBranchId = value
          break
        case "emergencyTypeId":
          filtvalues.emergencyTypeId = value
          break
        case "emergencySubTypeId":
          filtvalues.emergencySubTypeId = value
          break
        case "orderId":
          filtvalues.orderId = value
          break
        case "status":
          if (value !== "all") {
            filtvalues.status = value
          } else {
            delete filtvalues.status
          }
          break
        case "fromDate":
          filtvalues.fromDate = value
          if (value && !filtvalues.toDate) {
            const today = new Date().toISOString().split("T")[0]
            filtvalues.toDate = today
          }
          break
        case "toDate":
          filtvalues.toDate = value
          if (value && !filtvalues.fromDate) {
            const thirtyDaysAgo = new Date()
            thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)
            filtvalues.fromDate = thirtyDaysAgo.toISOString().split("T")[0]
          }
          break
        case "creationTime":
          filtvalues.creationTime = value
          break
        default:
          break
      }
    }
    setFilters(filtvalues)
    setPage(1) // Reset to first page when filters change
  }

  const getTranslatedText = (titleData, defaultText = "") => {
    try {
      if (typeof titleData === "string") {
        try {
          const parsed = JSON.parse(titleData)
          if (Array.isArray(parsed?.title)) {
            const langMap = { en: 1, ar: 2, ur: 3, fa: 4, ps: 5 }
            const langKey = langMap[i18n.language] || 1
            const titleObj = parsed.title.find(item => item && item[langKey])
            return titleObj ? titleObj[langKey] : defaultText
          }
          return parsed.title || defaultText
        } catch (e) {
          return titleData || defaultText
        }
      }

      if (titleData && typeof titleData === "object") {
        return getTranslatedText(titleData.title, defaultText)
      }

      return defaultText
    } catch (error) {
      console.error("Error in getTranslatedText:", error)
      return defaultText
    }
  }

  const handlePrevPage = () => {
    if (page > 1) {
      setPage(page - 1)
    }
  }

  const handleNextPage = () => {
    if (page < totalPages) {
      setPage(page + 1)
    }
  }
  //TODO:notification
  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Row>
            <Col xl={12}>
              <Card>
                {successMsg ? (
                  <Alert style={{ margin: "25px" }} color="success">
                    {successMsg}
                  </Alert>
                ) : null}
                <div style={{ margin: "25px" }}>
                  <div className="row">
                    <div style={{ margin: "25px" }}>
                      <div className="row">
                        <div className="form-group col-md-3 mt-2">
                          <Label className="form-label">
                            <Trans>Order ID</Trans>
                          </Label>
                          <Input
                            name="orderId"
                            className="form-control"
                            placeholder=""
                            type="number"
                            onChange={e =>
                              handleFilter("orderId", e.target.value)
                            }
                            onKeyDown={e =>
                              handleFilter("orderId", e.target.value)
                            }
                          ></Input>
                        </div>
                        <div className="form-group col-md-3 mt-2">
                          <Label className="form-label">
                            <Trans>Driver Name</Trans>
                          </Label>
                          <Select
                            name="driverId"
                            className="form-control"
                            placeholder="Select Driver"
                            options={drivers.map(driver => ({
                              value: driver.id,
                              label: driver.name,
                            }))}
                            onChange={selectedOption =>
                              handleFilter(
                                "driverId",
                                selectedOption ? selectedOption.value : ""
                              )
                            }
                            isClearable
                          />
                        </div>
                        {/* filter by from and to create date */}
                        <div className="form-group col-md-3 mt-2">
                          <Label className="form-label">
                            <Trans>From Date</Trans>
                          </Label>
                          <Input
                            name="fromDate"
                            className="form-control"
                            placeholder=""
                            type="date"
                            onChange={e =>
                              handleFilter("fromDate", e.target.value)
                            }
                            onKeyDown={e =>
                              handleFilter("fromDate", e.target.value)
                            }
                          ></Input>
                        </div>
                        <div className="form-group col-md-3 mt-2">
                          <Label className="form-label">
                            <Trans>To Date</Trans>
                          </Label>
                          <Input
                            name="toDate"
                            className="form-control"
                            placeholder=""
                            type="date"
                            onChange={e =>
                              handleFilter("toDate", e.target.value)
                            }
                            onKeyDown={e =>
                              handleFilter("toDate", e.target.value)
                            }
                          ></Input>
                        </div>
                        {/* by creation time */}
                        <div className="form-group col-md-3 mt-2">
                          <Label className="form-label">
                            <Trans>Creation Time</Trans>
                          </Label>
                          <Input
                            name="creationTime"
                            className="form-control"
                            placeholder=""
                            type="time"
                            onChange={e =>
                              handleFilter("creationTime", e.target.value)
                            }
                            onKeyDown={e =>
                              handleFilter("creationTime", e.target.value)
                            }
                          ></Input>
                        </div>
                        {/* partner */}
                        <div className="form-group col-md-3 mt-2">
                          <Label className="form-label">
                            <Trans>Partner</Trans>
                          </Label>
                          <Select
                            name="partnerId"
                            className="form-control"
                            placeholder="Select Partner"
                            options={partners.map(partner => ({
                              value: partner.id,
                              label: partner.name,
                            }))}
                            onChange={selectedOption =>
                              handleFilter(
                                "partnerId",
                                selectedOption ? selectedOption.value : ""
                              )
                            }
                            isClearable
                          />
                        </div>
                        {/* partner branch */}
                        <div className="form-group col-md-3 mt-2">
                          <Label className="form-label">
                            <Trans>Partner Branch</Trans>
                          </Label>
                          <Select
                            name="partnerBranchId"
                            className="form-control"
                            placeholder="Select Partner Branch"
                            options={partnersBranches.map(partnerBranch => ({
                              value: partnerBranch.id,
                              label: partnerBranch.name,
                            }))}
                            onChange={selectedOption =>
                              handleFilter(
                                "partnerBranchId",
                                selectedOption ? selectedOption.value : ""
                              )
                            }
                            isClearable
                          />
                        </div>
                        {/* emergency type */}
                        <div className="form-group col-md-3 mt-2">
                          <Label className="form-label">
                            <Trans>Emergency Type</Trans>
                          </Label>
                          <Select
                            name="emergencyTypeId"
                            className="form-control"
                            placeholder="Select Emergency Type"
                            options={emergencyTypes.map(emergencyType => ({
                              value: emergencyType.id,
                              label: getTranslatedText(emergencyType.title),
                            }))}
                            onChange={selectedOption =>
                              handleFilter(
                                "emergencyTypeId",
                                selectedOption ? selectedOption.value : ""
                              )
                            }
                            isClearable
                          />
                        </div>
                        {/* emergency sub type */}
                        <div className="form-group col-md-3 mt-2">
                          <Label className="form-label">
                            <Trans>Emergency Sub Type</Trans>
                          </Label>
                          <Select
                            name="emergencySubTypeId"
                            className="form-control"
                            placeholder="Select Emergency Sub Type"
                            options={emergencySubTypes.map(
                              emergencySubType => ({
                                value: emergencySubType.id,
                                label: getTranslatedText(
                                  emergencySubType.title
                                ),
                              })
                            )}
                            onChange={selectedOption =>
                              handleFilter(
                                "emergencySubTypeId",
                                selectedOption ? selectedOption.value : ""
                              )
                            }
                            isClearable
                          />
                        </div>
                        {/* status */}
                        <div className="form-group col-md-3 mt-2">
                          <Label className="form-label">
                            <Trans>Status</Trans>
                          </Label>
                          <Select
                            name="status"
                            className="form-control"
                            placeholder="Select Status"
                            options={statuses.map(status => ({
                              value: status.value,
                              label: status.label,
                            }))}
                            onChange={selectedOption =>
                              handleFilter(
                                "status",
                                selectedOption ? selectedOption.value : ""
                              )
                            }
                            isClearable
                          />
                        </div>
                      </div>
                    </div>
                    <CardBody>
                      <div className="table-responsive">
                        <Table className="table table-striped table-bordered mb-0">
                          <thead>
                            <tr>
                              <th>
                                <Trans>ID</Trans>
                              </th>
                              <th>
                                <Trans>Created Date</Trans>
                              </th>
                              <th>
                                <Trans>Created Time</Trans>
                              </th>
                              <th>
                                <Trans>Driver Name</Trans>
                              </th>
                              <th>
                                <Trans>Order ID</Trans>
                              </th>
                              <th>
                                <Trans>Partner</Trans>
                              </th>
                              <th>
                                <Trans>Partner Branch</Trans>
                              </th>
                              <th>
                                <Trans>Emergency Type</Trans>
                              </th>
                              <th>
                                <Trans>Emergency Sub Type</Trans>
                              </th>
                              <th>
                                <Trans>Status</Trans>
                              </th>
                              <th>
                                <Trans>Note</Trans>
                              </th>
                              <th>
                                <Trans>Comment</Trans>
                              </th>
                              <th>#</th>
                            </tr>
                          </thead>
                          <tbody>
                            {loading ? (
                              <tr>
                                <td colSpan="12" className="text-center">
                                  <div
                                    className="spinner-border text-primary"
                                    role="status"
                                  >
                                    <span className="sr-only">Loading...</span>
                                  </div>
                                </td>
                              </tr>
                            ) : (
                              driverEmergencies.map((emergency, index) => (
                                <tr key={index}>
                                  <td>{emergency.id}</td>
                                  <td>
                                    {moment(emergency.createdAt).format(
                                      "YYYY-MM-DD"
                                    )}
                                  </td>
                                  <td>{emergency.creationTime}</td>
                                  <td>
                                    <Link to={`/drivers/edit/${emergency.driver.id}`}>
                                      {emergency.driver.name}
                                    </Link>
                                  </td>
                                  <td>
                                    <Link to={`/orders/edit/${emergency.order.id}`}>
                                      {emergency.order.id}
                                    </Link>
                                  </td>
                                  <td>
                                    {emergency.order.partner?.name
                                      ? emergency.order.partner.name
                                      : ""}
                                  </td>
                                  <td>
                                    {emergency.order.partnerBranch?.name
                                      ? emergency.order.partnerBranch.name
                                      : ""}
                                  </td>
                                  <td>
                                    {getTranslatedText(
                                      emergency.emergencyType.title,
                                      "No title available"
                                    )}
                                  </td>
                                  <td>
                                    {getTranslatedText(
                                      emergency.emergencySubType.title,
                                      "No title available"
                                    )}
                                  </td>
                                  <td>{emergency.status}</td>
                                  <td>{emergency.notes}</td>
                                  <td>{emergency.comments}</td>
                                  <td>
                                    {emergency.canEdit && (
                                      <Link
                                        to={`/driver-emergencies/edit/${emergency.id}`}
                                      >
                                        <button className="btn btn-sm btn-info">
                                          <i className="dripicons-pencil"></i>{" "}
                                          &nbsp;
                                          <Trans>Edit</Trans>
                                        </button>
                                      </Link>
                                    )}
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </Table>
                      </div>
                      <br></br>
                      <div className="text-center">
                        {/* Pagination controls */}
                        <button
                          className="btn btn-sm btn-dark"
                          onClick={handlePrevPage}
                          disabled={page === 1}
                        >
                          <Trans>Previous</Trans>
                        </button>
                        &nbsp;
                        <button
                          className="btn btn-sm btn-dark"
                          onClick={handleNextPage}
                          disabled={page === totalPages}
                        >
                          <Trans>Next</Trans>
                        </button>
                      </div>
                    </CardBody>
                  </div>
                </div>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </React.Fragment>
  )
}
export default DriverEmergenciesList
